/**
 * Which identity provider a typed email address signs in against.
 *
 * The sign-in screen asks for an address instead of asking someone to pick a provider,
 * because the choice has no visible consequence to the person making it: both providers
 * redirect to a Microsoft page, and on the MIKE Cloud path that page names neither DHI nor
 * MIKE nor the tenant. Asking a customer to classify themselves against a distinction only
 * we can see was solving our problem with their attention.
 *
 * What is underneath stays two providers, because the answer decides which service mints
 * the token, not merely which login page appears. MIKE Cloud issues its own tokens from its
 * own JWKS and gates on site membership before authenticating; DHI's tenant issues
 * Microsoft's. So this routes, and everything downstream of it is unchanged.
 *
 * The rule is deliberately asymmetric. A provider that claims domains wins for those
 * domains; the provider that claims none takes everything else. The DHI side is a finite
 * list somebody maintains, and the customer side is the open world: a new water utility
 * must never need a configuration change before its people can sign in.
 *
 * Two things this file must not become. It must not be the only way in, because every rule
 * here is a guess about somebody's email address and being wrong would otherwise strand a
 * colleague at an unlisted domain on a provider that has never heard of them; the
 * `/signin/<provider>` routes exist for exactly that, as a link support can hand out. And
 * it must not touch identity. The backend derives a person's id from `uuid5(namespace, email)` over the
 * unnormalised claim in their token, so `normaliseForRouting` is named the way it is as a
 * warning: lowercasing anything on the identity path would silently split one person's
 * history in two.
 */

import type { AuthProviderConfig } from '../types/api';

/**
 * Lowercased and whitespace-trimmed, for routing only. Never for identity.
 *
 * Addresses arrive pasted with trailing spaces and typed in whatever case the keyboard was
 * in, and neither should change which provider someone reaches.
 */
export function normaliseForRouting(email: string): string {
  return email.trim().toLowerCase();
}

/** The domain part of an already-normalised address, or `''` when there isn't one. */
function domainOf(normalised: string): string {
  const at = normalised.lastIndexOf('@');
  return at === -1 ? '' : normalised.slice(at + 1);
}

/**
 * Whether this is worth trying to route, which is a lower bar than whether it is valid.
 *
 * Deliberately loose. The real check is the identity provider's, and anything stricter
 * than this rejects addresses that genuinely exist: quoted local parts, apostrophes,
 * plus-addressing, long new top-level domains. All this has to catch is the person who
 * has clearly not finished typing, so that they get a sentence next to the field rather
 * than a redirect to a provider that will refuse them for reasons it cannot explain.
 */
export function looksLikeEmail(value: string): boolean {
  const v = normaliseForRouting(value);
  if (v.length === 0 || /\s/.test(v)) return false;
  const at = v.lastIndexOf('@');
  // An `@` at either end leaves one half empty.
  if (at <= 0 || at === v.length - 1) return false;
  const domain = v.slice(at + 1);
  return domain.includes('.') && !domain.startsWith('.') && !domain.endsWith('.');
}

/**
 * The provider whose domains claim this address, or the one that claims none.
 *
 * Matched exactly rather than by suffix. Suffix matching would quietly need a dot-boundary
 * check to stop `notdhigroup.com` matching `dhigroup.com`, and a subdomain that turns out
 * to exist is one entry in a setting, which is cheaper than a rule nobody can eyeball.
 *
 * Returns null only when no provider claims the domain and none is open-world, which the
 * backend's own test forbids. The screen still handles it, because a null here would
 * otherwise be an unexplained dead click.
 */
export function providerForEmail(
  email: string,
  providers: AuthProviderConfig[],
): AuthProviderConfig | null {
  const domain = domainOf(normaliseForRouting(email));
  if (!domain) return null;
  const claimed = providers.find((p) =>
    (p.email_domains ?? []).some((d) => d.trim().toLowerCase() === domain),
  );
  if (claimed) return claimed;
  return providers.find((p) => (p.email_domains ?? []).length === 0) ?? null;
}
